import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Plus } from 'lucide-react';

import ProtectionIcon from '@/assets/features/protection.png';
import ShippingIcon from '@/assets/features/shipping.png';
import ClaimsIcon from '@/assets/features/claims.png';
import FraudIcon from '@/assets/features/fraud.png';
import ReturnsIcon from '@/assets/features/returns.png';

import ProtectionIconActive from '@/assets/features/protectionActive.png';
import ShippingIconActive from '@/assets/features/shippingActive.png';
import ClaimsIconActive from '@/assets/features/claimsActive.png';
import FraudIconActive from '@/assets/features/fraudActive.png';
import ReturnsIconActive from '@/assets/features/returnsActive.png';

interface FeatureContent {
  title: string;
  description: string;
  color: string;
  rgb: string;
  content: {
    heading: string;
    subheading: string;
  };
  link: string;
}

const features: FeatureContent[] = [
  {
    title: 'Protection',
    description: 'Coverage for accidents & more',
    color: 'cyan',
    rgb: '0,216,255',
    content: {
      heading: 'Protect what matters most with Protega Product Protection.',
      subheading:
        'Boost revenue and brand loyalty by offering protection from product failure and accidental damage.',
    },
    link: '/protection#'
  },
  {
    title: 'Shipping',
    description: 'Protection for loss, damage & theft',
    color: 'emerald',
    rgb: '52,211,153',
    content: {
      heading: 'Make sure it gets there with Protega Shipping Protection.',
      subheading:
        'Cut operational costs and eliminate stress caused by porch pirates and damaged deliveries.',
    },
    link: '/shipping#'
  },
  {
    title: 'Claims',
    description: 'Automated claim support',
    color: 'amber',
    rgb: '251,191,36',
    content: {
      heading: 'Nurture customer loyalty with a self-service claims experience.',
      subheading:
        'Manage all delivery and fulfillment errors with a branded customer experience configured to reduce fraud and nurture brand loyalty.',
    },
    link: '/claims#'
  },
  {
    title: 'Fraud',
    description: 'AI-powered fraud detection',
    color: 'red',
    rgb: '248,113,113',
    content: {
      heading: 'Let AI help you stop bad actors from impacting your bottom line.',
      subheading:
        'A powerful post-purchase fraud mitigation platform is built seamlessly into every step of the customer journey, stopping fraudsters in their tracks.',
    },
    link: '/fraud#'
  },
  {
    title: 'Returns',
    description: 'Seamless returns & exchanges',
    color: 'purple',
    rgb: '192,132,252',
    content: {
      heading: 'Find cost savings through intelligent returns and exchanges.',
      subheading:
        'Offer a personalized, customer-centered returns policy that increases lifetime value while lowering costs.',
    },
    link: '/returns#'
  },
];

const getIcon = (title: string, isActive: boolean): string => {
  if (isActive) {
    switch (title) {
      case 'Protection': return ProtectionIconActive;
      case 'Shipping': return ShippingIconActive;
      case 'Claims': return ClaimsIconActive;
      case 'Fraud': return FraudIconActive;
      case 'Returns': return ReturnsIconActive;
      default: return ProtectionIconActive;
    }
  } else {
    switch (title) {
      case 'Protection': return ProtectionIcon;
      case 'Shipping': return ShippingIcon;
      case 'Claims': return ClaimsIcon;
      case 'Fraud': return FraudIcon;
      case 'Returns': return ReturnsIcon;
      default: return ProtectionIcon;
    }
  }
};

const slideVariants = {
  hidden: (direction: number) => ({ y: direction * 50, opacity: 0 }),
  visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
  exit: (direction: number) => ({ y: direction * 50, opacity: 0, transition: { duration: 0.5 } }),
};

const fadeInVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const accordionContentVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1, transition: { duration: 0.3 } },
  exit: { height: 0, opacity: 0, transition: { duration: 0.3 } },
};

interface FeaturesProps {
  showMainHeading?: boolean;
}

const Features: React.FC<FeaturesProps> = ({
  showMainHeading = false,
}) => {
  const [activeFeature, setActiveFeature] = useState<number>(0);
  const [direction, setDirection] = useState(0);

  const changeFeature = (idx: number) => {
    if (idx === activeFeature) {
      setActiveFeature(-1);
      setDirection(0);
    } else {
      setDirection(idx > activeFeature ? -1 : 1);
      setActiveFeature(idx);
    }
  };

  return (
    <motion.section
      className="tw-bg-black tw-px-6 md:tw-px-12 tw-flex tw-flex-col tw-items-start md:tw-items-center tw-justify-center tw-overflow-hidden tw-min-h-screen"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeInVariant}
    >
      {/* Main heading */}
      {showMainHeading && (
        <div className="tw-max-w-5xl tw-text-center tw-mb-12">
          <h2 className="tw-text-4xl md:tw-text-5xl lg:tw-text-6xl tw-font-bold tw-text-white leading-tight">
            A single platform for every
            <span className="tw-text-[#00d8ff]"> post‑purchase interaction.</span>
          </h2>
        </div>
      )}

      {/* Small “Resolve customer issues…” heading (mobile) */}
      <div className="tw-block lg:tw-hidden tw-w-full tw-max-w-3xl tw-mx-auto tw-text-center tw-mb-8">
        <h3 className="tw-text-xl tw-font-bold tw-text-white leading-tight">
          Resolve customer issues<br />
          with one dynamic solution.
        </h3>
        <p className="tw-text-gray-300 tw-text-sm md:tw-text-base tw-max-w-lg tw-mx-auto tw-mt-4 leading-relaxed">
          Drive revenue, reduce costs, and improve post-purchase customer interactions
          through AI and automation.
        </p>
      </div>

      {/* Mobile accordion */}
      <div className="tw-w-full tw-max-w-3xl tw-mx-auto tw-block lg:tw-hidden tw-my-4">
        {features.map((feature, idx) => {
          const isActive = idx === activeFeature;
          const glowRGB = feature.rgb;
          const iconSrc = getIcon(feature.title, isActive);

          return (
            <div
              key={feature.title}
              className="tw-transition-all tw-duration-500 tw-mb-4"
              style={{
                backgroundColor: isActive ? `rgba(${glowRGB}, 1)` : "#202020",
                borderRadius: 16,
                boxShadow: isActive ? `0 0 40px rgba(${glowRGB}, 0.9)` : undefined,
              }}
            >
              <button
                onClick={() => changeFeature(idx)}
                className="tw-flex tw-items-center tw-w-full tw-transition-all tw-duration-200 tw-text-left tw-py-4 tw-justify-between"
                style={{
                  backgroundColor: "transparent",
                  borderRadius: isActive ? "16px 16px 0 0" : 16,
                  padding: "12px 24px",
                  color: isActive ? "#000" : "#fff",
                }}
              >
                <div className="tw-flex tw-items-center">
                  <img src={iconSrc} alt={feature.title} className="tw-h-10 tw-mr-4" />
                  <div>
                    <div className={clsx("tw-font-semibold tw-text-lg", isActive ? "tw-text-black" : "tw-text-white")}>
                      {feature.title}
                    </div>
                    <div className={clsx("tw-text-sm", isActive ? "tw-text-black/90" : "tw-text-gray-300")}>
                      {feature.description}
                    </div>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: isActive ? 45 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ originX: 0.5, originY: 0.5 }}
                >
                  <Plus size={20} strokeWidth={2} className={isActive ? "tw-text-black" : "tw-text-white"} />
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.div
                    variants={accordionContentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="tw-overflow-hidden tw-p-6 tw-pt-2 tw-pb-10"
                    style={{ borderRadius: "0 0 16px 16px" }}
                  >
                    <h3 className="tw-text-black tw-text-[20px] tw-font-extrabold leading-snug">
                      {feature.content.heading}
                    </h3>
                    <p className="tw-text-black tw-text-base tw-font-medium leading-relaxed tw-pb-3">
                      {feature.content.subheading}
                    </p>

                    <Link
                      to={feature.link}
                      className="tw-inline-flex tw-bg-black tw-text-white tw-text-sm sm:tw-text-base tw-font-semibold tw-py-2 tw-px-4 tw-rounded-full hover:tw-bg-gray-800 tw-transition">
                      Learn More
                    </Link>

                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Desktop */}
      <div className="tw-w-full tw-flex-col tw-justify-center tw-my-16 md:tw-flex-row tw-gap-6 lg:tw-gap-12 tw-h-full tw-max-w-7xl tw-hidden lg:tw-flex">
        {/* Left panel */}
        <motion.div
          className="tw-w-full lg:tw-w-[35%] tw-flex tw-flex-col tw-justify-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariant}
        >
          {/* Small heading on desktop */}
          <div className="tw-hidden sm:tw-block tw-w-full tw-max-w-3xl tw-mx-auto tw-text-start tw-mb-8">
            <h3 className="tw-text-2xl tw-font-bold tw-mb-2 tw-text-white leading-tight">
              Resolve customer issues<br />
              with one dynamic solution.
            </h3>
            <p className="tw-text-gray-300 tw-text-base tw-max-w-lg tw-mb-6 leading-relaxed">
              Drive revenue, reduce costs, and improve post-purchase customer interactions
              through AI and automation.
            </p>
          </div>

          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
            {features.map((feature, idx) => {
              const isActive = idx === activeFeature;
              const glowRGB = feature.rgb;
              const iconSrc = getIcon(feature.title, isActive);

              return (
                <button
                  key={feature.title}
                  onClick={() => changeFeature(idx)}
                  className="tw-flex tw-items-center tw-w-full tw-transition-all tw-duration-200 tw-text-left"
                  style={{
                    backgroundColor: isActive ? `rgba(${glowRGB},1)` : 'transparent',
                    borderRadius: isActive ? 24 : 16,
                    boxShadow: isActive ? `0 0 20px rgba(${glowRGB},0.9)` : undefined,
                    padding: '12px 24px',
                    color: isActive ? '#000' : '#fff',
                  }}
                >
                  <img src={iconSrc} alt={feature.title} className="tw-h-10 tw-mr-4" />
                  <div>
                    <div className={clsx('tw-font-semibold tw-text-lg', isActive ? 'tw-text-black' : 'tw-text-white')}>
                      {feature.title}
                    </div>
                    <div className={clsx('tw-text-sm', isActive ? 'tw-text-black/90' : 'tw-text-gray-300')}>
                      {feature.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* <div className="tw-w-[10%] tw-hidden lg:tw-block" /> */}

        {/* Right panel */}
        <motion.div
          className="tw-w-full lg:tw-w-[50%] tw-flex tw-items-center tw-justify-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariant}
        >
          <AnimatePresence custom={direction} mode="wait">
            {features.map((feature, idx) => {
              if (idx !== activeFeature) return null;
              return (
                <motion.div
                  key={idx}
                  custom={direction}
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="tw-h-full tw-rounded-[32px] tw-justify-between tw-items-stretch tw-w-full tw-max-w-[540px] tw-p-16"
                  style={{
                    backgroundColor: `rgba(${feature.rgb}, 1)`,
                    boxShadow: `0 0 30px rgba(${feature.rgb}, 0.9)`,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div className="tw-flex tw-flex-col tw-justify-center tw-gap-6 tw-h-full">
                    <h3 className="tw-text-black tw-text-[24px] sm:tw-text-[28px] md:tw-text-[32px] lg:tw-text-[40px] tw-font-extrabold tw-leading-snug tw-mb-4">
                      {feature.content.heading}
                    </h3>
                    <p className="tw-text-black/90 tw-text-base sm:tw-text-lg md:tw-text-xl tw-font-medium tw-leading-relaxed">
                      {feature.content.subheading}
                    </p>
                    <Link
                      to={feature.link}
                      className="tw-inline-flex tw-self-start tw-bg-black tw-text-white tw-text-sm sm:tw-text-base tw-font-semibold tw-py-2 sm:tw-py-3 tw-px-6 sm:tw-px-8 tw-rounded-full hover:tw-bg-black/80 tw-transition-all tw-shadow-md tw-mt-6"
                    >
                      Learn More
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Features;